/**
 * @license Copyright (c) 2003-2022, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import Command from '@ckeditor/ckeditor5-core/src/command';
import findAttributeRange from '@ckeditor/ckeditor5-typing/src/utils/findattributerange'; 	
import getRangeText from './utils.js';
import { toMap } from '@ckeditor/ckeditor5-utils';						

export default class LinklawCommand extends Command {
    refresh() {
		const model = this.editor.model;
		const selection = model.document.selection;
		const firstRange = selection.getFirstRange();

		// When the selection is collapsed, the command has a value if the caret is in an linklaw.
		if ( firstRange.isCollapsed ) {
			if ( selection.hasAttribute( 'linklaw' ) ) {
				const attributeValue = selection.getAttribute( 'linklaw' );
				const attributeValueN = selection.getAttribute( 'articlelaw' );
				const attributeValueT = selection.getAttribute( 'tooltip' );
				const attributeValueId = selection.getAttribute( 'id' );

				// Find the entire range containing the linklaw under the caret position.
				const linklawRange = findAttributeRange( selection.getFirstPosition(), 'linklaw', attributeValue, model );

				this.value = {
					span: getRangeText( linklawRange ),
					dataLinklaw: attributeValue,
					dataArticlelaw: attributeValueN,
					dataTooltip: attributeValueT,
					dataId: attributeValueId,
					range: linklawRange
				};
			} else {
				this.value = null;
			}
		}
		// When the selection is not collapsed, the command has a value if the selection contains a subset of a single linklaw
		// or an entire linklaw.
		else {
			if ( selection.hasAttribute( 'linklaw' ) ) {
				const attributeValue = selection.getAttribute( 'linklaw' );
				const attributeValueN = selection.getAttribute( 'articlelaw' );
				const attributeValueT = selection.getAttribute( 'tooltip' );
				const attributeValueId = selection.getAttribute( 'id' );

				// Find the entire range containing the linklaw under the caret position.
				const linklawRange = findAttributeRange( selection.getFirstPosition(), 'linklaw', attributeValue, model );

				if ( linklawRange.containsRange( firstRange, true ) ) {
					this.value = {
						span: getRangeText( firstRange ),
						dataLinklaw: attributeValue,
						dataArticlelaw: attributeValueN,
						dataTooltip: attributeValueT,
						dataId: attributeValueId,
						range: firstRange
					};
				} else {
					this.value = null;
				}
			} else {
				this.value = null;
			}
		}

		// The command is enabled when the "linklaw" attribute can be set on the current model selection.
		this.isEnabled = model.schema.checkAttributeInSelection( selection, 'linklaw' );
	}

	execute( { span, dataLinklaw, dataArticlelaw, dataTooltip, dataId } ) {
		const model = this.editor.model;
		const selection = model.document.selection;

		model.change( writer => {
			// If selection is collapsed then update the selected linklaw or insert a new one at the place of caret.
			if ( selection.isCollapsed ) {
				// When a collapsed selection is inside text with the "linklaw" attribute, update its text and dataLinklaw.
				if ( this.value ) {
					const { end: positionAfter } = model.insertContent(
						writer.createText( span, { linklaw: dataLinklaw, articlelaw: dataArticlelaw, tooltip: dataTooltip, id: dataId } ),
						this.value.range
					);
					// Put the selection at the end of the inserted linklaw.
					writer.setSelection( positionAfter );
				}
				// If the collapsed selection is not in an existing linklaw, insert a text node with the "linklaw" attribute
				// in place of the caret. Because the selection is collapsed, the attribute value will be used as a data for text.
				// If the linklaw is empty, do not do anything.
				else if ( span !== '' ) {
					const firstPosition = selection.getFirstPosition();

					// Collect all attributes of the user selection (could be "bold", "italic", etc.)
					const attributes = toMap( selection.getAttributes() );

					// Put the new attribute to the map of attributes.
					attributes.set( 'linklaw', dataLinklaw );
					attributes.set( 'articlelaw', dataArticlelaw );
					attributes.set( 'tooltip', dataTooltip );
					attributes.set( 'id', dataId );
					

					// Inject the new text node with the linklaw text with all selection attributes.
					const { end: positionAfter } = model.insertContent( writer.createText( span, attributes ), firstPosition );

					// Put the selection at the end of the inserted linklaw. Using an end of a range returned from
					// insertContent() just in case nodes with the same attributes were merged.
					writer.setSelection( positionAfter );
				}

				// Remove the "linklaw" attribute attribute from the selection. It stops adding a new content into the linklaw
				// if the user starts to type.
				writer.removeSelectionAttribute( 'linklaw', 'articlelaw' );
			} else {
				// If the selection has non-collapsed ranges, change the attribute on nodes inside those ranges
				// omitting nodes where the "linklaw" attribute is disallowed.
				const ranges = model.schema.getValidRanges( selection.getRanges(), 'linklaw', 'articlelaw', 'tooltip' );

				for ( const range of ranges ) {
					writer.setAttribute( 'linklaw', dataLinklaw, range );
					writer.setAttribute( 'articlelaw', dataArticlelaw, range );
					writer.setAttribute( 'tooltip', dataTooltip, range );
					writer.setAttribute( 'id', dataId, range );
				}
			}
		} );
	}
}